import React, {useState} from 'react';
import api from '../../common/api';
import { Dialog, DialogTitle, DialogActions, Button } from "@material-ui/core";
import makeStyles from "@material-ui/styles/makeStyles";
import {MaterialTable, PageHeader} from '../../components';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Switch from '@material-ui/core/Switch';
import {useGlobalState} from '../../state';
import Alert from '@material-ui/lab/Alert';
import {version} from "../../common/enums"
import {isAllOnboardingTasksCompleted} from "../../common/functions"
import UpdateIcon from '@material-ui/icons/Update';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import Mobile from './components/mobile';
const useStyles = makeStyles(theme => ({
    root: props => ({
        // margin: props.isDesktop ? theme.spacing(4) : theme.spacing(1)
    }),
    topBar: {
        marginBottom: theme.spacing(2)
    },
    link: {
        display: 'flex',
    },
    icon: {
        marginRight: theme.spacing(0.5),
        width: 20,
        height: 20,
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    helpIcon: {
        fontSize: 18,
        marginLeft: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        cursor: 'pointer'
    },
    helpTooltip: {
        fontSize: '0.8rem',
        lineHeight: 1.6,
        maxWidth: 320,
        padding: theme.spacing(1.25, 1.5)
    },
    tooltipText: {
        display: 'block'
    },
    tooltipEg: {
        display: 'block',
        marginTop: theme.spacing(1),
        opacity: 0.85
    }
}));
// Friendly name and help text for each account configuration switch. Only what the user reads
// comes from here - `name` posted to updateConfiguration is still the raw config key.
const configMeta = {
    feed_section: {
        label: "Feed Section",
        help: "Shows the Feed menu and its screens.",
        example: "Turn off if you do not track feed in the app."
    },
    auto_sales: {
        label: "Auto Sales",
        help: "Creates the egg sale automatically from Daily Entry.",
        example: "Enter egg sales in Daily Entry and the sale is added under Sales for you."
    },
    auto_purchase: {
        label: "Auto Purchase",
        help: "Creates the feed purchase automatically from Daily Entry.",
        example: "Select the feed type and enter feed received in Daily Entry; the purchase is added under Purchases at the Daily Feed Rate."
    },
    truckeggs_as_goodEggs: {
        label: "Add Truck Damaged & Broken to Good Eggs",
        help: "Eggs broken or damaged while loading the truck are counted as good eggs, not as broken.",
        example: "1,000 eggs produced, 30 truck broken or damage. ON - Good Eggs 1,030. OFF - Good Eggs 1000 and Broken 30."
    },
    onboarding_tasks: {
        label: "Onboarding Tasks",
        help: "Shows the Get Started checklist until all the setup tasks are completed."
    }
};

const configRow = (key, value) => ({
    name: key,
    value: value,
    label: configMeta[key]?.label || key.toUpperCase().replace(/_/g, ' '),
    help: configMeta[key]?.help || "",
    example: configMeta[key]?.example || ""
});

const ConfigView = ({showError}) => {
    const [{user}, dispatch] = useGlobalState();
    // console.log("user from config",user)
    const [loading, setLoading] = useState(false);
    const [configData, setConfigData] = useState([])
    const [errorMessage, setErrorMessage] = useState('')
    const [showAllTasksCompletedPopup, setShowAllTasksCompletedPopup] = useState(false)
    const [updateMessage, setUpdateMessage] = useState(false)
    function tableData(){
        showError('')
        let arr=[];
        for (const [key, value] of Object.entries(user.Config)) {
            if(user.versionID === version.proPlus){
                if (key !== "auto_sales" && key !== "auto_purchase") { // auto_sales / auto_purchase configs are not available for pro plus users
                    arr.push(configRow(key, value));
                }
            } else  if((user.versionID === version.pro) && ((key === "truckeggs_as_goodEggs" || key === "onboarding_tasks") )) {
                arr.push(configRow(key, value));
            } else if((user.versionID === version.lite) && ((key === "onboarding_tasks") )){
                arr.push(configRow(key, value));
            } else if((user.versionID === version.pro) && ((key === "auto_sales" || key === "auto_purchase") )){
                arr.push(configRow(key, value));
            }
        }
        setConfigData(arr);
    }
    if(configData.length===0){
        tableData();
    }
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true
    });
    const classes = useStyles({isDesktop});

    const fetchAccountTasksAndFarms = React.useCallback(async () => {
        try {
            const tasksResponse = await api.post('/getOnboardingTasks', { versionID: user.versionID });
            let farmsResponse = await api.get('farm').then(res => res).catch(err => {
                // if there is no farm, return []
                if (err.message === "No data found" && err.ErrorCode === 404) {
                    return [];
                } else {
                    throw err;
                }
            });
    
            if (tasksResponse.tasks !== user.onboardingTasks || farmsResponse !== user.farms) {
                dispatch({
                    type: 'setProfile',
                    user: {
                        ...user,
                        onboardingTasks: tasksResponse.tasks,
                        farms: farmsResponse,
                    }
                });
            }
        } catch (err) {
            console.log("Error fetching tasks and farms: ", err);
        }
    }, [dispatch, user]);

    const onUpdatingConfig = (config) => {
        setLoading(true);
        showError('');
        const req = {
            name : config.name,
            value : !config.value
        };

        // if user trying to turn on onboarding tasks and all tasks are already completed then show popup
        if (req.name === "onboarding_tasks" && req.value && user.onboardingTasks && isAllOnboardingTasksCompleted(user.onboardingTasks)) {
            setShowAllTasksCompletedPopup(true)
            setLoading(false);
            return;
        }

        const response = api.post(`updateConfiguration`, req);
        response.then((responseValue) => {
            // if user activating onboarding tasks and all tasks are not completed then fetch and update onboarding tasks and farms in local storage
            if (req.name === "onboarding_tasks" && req.value && responseValue.message !== "All Onboarding Tasks Completed") {
                fetchAccountTasksAndFarms();
            }

            // show tasks already completed popup if all the tasks are completed
            if (responseValue.message === "All Onboarding Tasks Completed"){
                setShowAllTasksCompletedPopup(true)
            }

            // if Configuration updated then update the state
            if (responseValue.message === "Configuration Updated Successfully"){
                dispatch({type:"config",payload:config});
                tableData();
                setUpdateMessage(true);
            }
            // console.log("state config after updating",user.Config)
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'No data found')
                    setErrorMessage('Subscriber not Updated');
                else
                    setErrorMessage(err.message)
            } else {
                setErrorMessage('Unknown Error')
            }
        }).finally(()=>{
            setLoading(false);
        })
    };

    const handleIsToggleChange = (rowData) =>{
        onUpdatingConfig(rowData);
    };
    const removeErrorMessage = () =>{
        setErrorMessage('');
    }
    const deleteUpdateMessage = () =>{
        setUpdateMessage(false);
    }

    return <div className={classes.root}>
        {errorMessage ?
    <Alert severity="error" onClick={removeErrorMessage}>{errorMessage}</Alert>
    : ''}
        <PageHeader title='Configuration' />
        {updateMessage ? <Chip  color="secondary" onDelete={deleteUpdateMessage} icon={<UpdateIcon/>} label="Updated. Please logout and login again for your changes to become effective" /> : ""}
        <MaterialTable
            isLoading={loading}
            columns={[
                // {title: 'id', field: 'id', hidden: true},
                {hidden: isDesktop,
                render : rowData => <Mobile data={rowData} onToggle={()=>handleIsToggleChange(rowData)}/>},
                {title: 'Name', field: "label", hidden: !isDesktop, editable: 'never',
                render : rowData=>{
                    return <span className={classes.nameCell}>
                        {rowData.label}
                        {rowData.help ?
                            <Tooltip
                                title={<React.Fragment>
                                    <span className={classes.tooltipText}>{rowData.help}</span>
                                    {rowData.example ?
                                        <span className={classes.tooltipEg}><b>Eg:</b> {rowData.example}</span>
                                    : ''}
                                </React.Fragment>}
                                arrow
                                placement="top"
                                enterTouchDelay={0}
                                leaveTouchDelay={10000}
                                classes={{tooltip: classes.helpTooltip}}
                            >
                                <HelpOutlineIcon className={classes.helpIcon}/>
                            </Tooltip>
                        : ''}
                    </span>
                }},
                {title: 'Show/Hide', field: 'value', hidden: !isDesktop, editable: 'never',
                render : rowData=>{
                    return <Switch
                        checked={rowData.value}
                        onChange={()=>handleIsToggleChange(rowData)}
                        color="primary"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                    />
                }},
            ]}
            data={configData}
            title='Configuration Table'
            options={{
                actionsColumnIndex: -1,
                search: false,
                showTitle: false,

                toolbar: true,
                header: isDesktop,
            }}
        />
        <Dialog open={showAllTasksCompletedPopup}>
            <DialogTitle>you have already completed all onboarding tasks</DialogTitle>
            <DialogActions>
                <Button onClick={()=>setShowAllTasksCompletedPopup(false)} color="secondary">
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    </div>
}

export default ConfigView;